import { Children, cloneElement, memo, useCallback, useMemo, useRef } from "react";
import { Modal, type ModalRef } from "react-better-html";

import { useI18n } from "../../utils/i18n";

type ConfirmationWrapperProps = {
   title?: string;
   description?: string;
   message?: string;
   continueButtonText?: string;
   continueButtonLoaderName?: string;
   isDestructive?: boolean;
   onCancel?: () => void;
   onContinue?: () => void;
   children?: React.ReactNode;
};

function ConfirmationWrapper({
   title,
   description,
   message,
   continueButtonText,
   continueButtonLoaderName,
   isDestructive,
   onCancel,
   onContinue,
   children,
}: ConfirmationWrapperProps) {
   const { t } = useI18n();

   const modalRef = useRef<ModalRef>(null);

   const onClickChild = useCallback(() => {
      modalRef.current?.open();
   }, []);

   const wrappedChildren = useMemo(
      () =>
         Children.map(children, (child: any) => {
            if (child == null || typeof child === "string" || typeof child === "number") {
               return child;
            }

            const childProps = (child as React.ReactElement).props as Record<string, any>;
            const existingOnClick = childProps?.onClick as (() => void) | undefined;

            return cloneElement(
               child as React.ReactElement,
               {
                  onClick: () => {
                     existingOnClick?.();
                     onClickChild();
                  },
               } as any,
            );
         }),
      [children, onClickChild],
   );

   const ModalTag = isDestructive ? Modal.destructive : Modal.confirmation;

   return (
      <>
         {wrappedChildren}

         <ModalTag
            title={title ?? t((l) => l.components.confirmationWrapper.title)}
            description={description}
            message={message ?? t((l) => l.components.confirmationWrapper.message)}
            continueButtonText={continueButtonText ?? t((l) => l.common.continue)}
            continueButtonLoaderName={continueButtonLoaderName}
            cancelButtonText={t((l) => l.common.cancel)}
            onCancel={onCancel}
            onClose={onCancel}
            onContinue={onContinue}
            onDelete={onContinue}
            ref={modalRef}
         />
      </>
   );
}

export default memo(ConfirmationWrapper);
